import React, { FunctionComponent, useEffect, useState } from "react";
import styles from "./Logos.module.scss";
import LogosProps from "./LogosProps.interface";

import Logo from "./components/Logo/Logo";

import { motion, useAnimation } from "framer-motion";
import { useHistory } from "react-router-dom";
import { useInView } from "react-intersection-observer";
import BubbleBackground from "components/BubbleBackground/BubbleBackground";
import combineClasses from "helpers/combine-classes";
import getWrapperClasses from "blocks/helpers/getWrapperClasses";
import isBrowserContext from "helpers/isBrowserContext";
import useAnchorIdentifier from "hooks/useAnchorIdentifier";
import useFilter from "blocks/hooks/useFilter";
import Dictionary from "components/Dictionary/Dictionary";

const Logos: FunctionComponent<LogosProps> = (props) => {
    const history = useHistory();

    const wrapperClasses = getWrapperClasses(props.settings);
    const identifier = useAnchorIdentifier(props.settings.anchorIdentifier);

    const [items, setItems] = useState(props.content.logos);

    const [filters, viewState] = useFilter(history.location.pathname);
    const [currentFilters, setCurrentFilters] = useState<any[]>([]);

    const clearFilters = () => {
        setCurrentFilters([]);

        // Setting URL Params
        history.replace({
            search: undefined,
        });

        setItems(props.content.logos);
    };

    const noFiltersClass = !currentFilters || (currentFilters && currentFilters.length === 0) ? styles.is_active : undefined;

    const handleTagClick = (filter: any) => {
        const filterId = filter.id.toString();

        // Setting current filter
        if (typeof (filterId) !== "undefined" && filterId !== null) {
            if (currentFilters && currentFilters.length > 0 && currentFilters[0] === filterId) {
                setCurrentFilters([]);

                // Setting URL Params
                history.replace({
                    search: undefined,
                });

                setItems(props.content.logos);
            }
            else {
                setCurrentFilters([filterId]);

                // Setting URL Params
                history.replace({
                    pathname: location.pathname,
                    search: `?f=${filter.id}`,
                    hash: location.hash
                });

                const itemsFiltered = props.content.logos?.filter((item) => {
                    const givenFilters = item.content.filters && item.content.filters.map(x => x._id.toString());
                    return givenFilters?.includes(filterId);
                });

                setItems(itemsFiltered);
            }
        }
    };

    useEffect(() => {
        const query = new URLSearchParams(history.location.search);
        const filter = query.get("f");

        // Setting current filter
        if (typeof (filter) !== "undefined" && filter !== null) {
            setCurrentFilters([filter]);

            const itemsFiltered = props.content.logos?.filter((item) => {
                const givenFilters = item.content.filters && item.content.filters.map(x => x._id?.toString());
                return givenFilters?.includes(filter);
            });

            setItems(itemsFiltered);
        }
    }, []);

    // ANIMATION
    const controls = useAnimation();
    const { ref, inView } = useInView({ threshold: 0.3 });

    const animateUp = {
        visible: { y: 0, opacity: 1 },
        hidden: { y: 80, opacity: 0 }
    };
    const animateDown = {
        visible: { y: 0, opacity: 1 },
        hidden: { y: -80, opacity: 0 }
    };

    const selectAnimation = props.settings.animation && props.settings.animation.length > 0 ? props.settings.animation[0].content._contentType : undefined;
    const selectVariants = selectAnimation === "animateUp" ? animateUp : selectAnimation === "animateDown" ? animateDown : undefined;
    const repeatAnimation = props.settings.animation && props.settings.animation.length > 0 ? props.settings.animation[0].content.repeatAnimation : false;

    useEffect(() => {
        if (inView) {
            controls.start("visible");
        }
        if (!inView && repeatAnimation) {
            controls.start("hidden");
        }
    }, [controls, inView]);

    return (
        <motion.section className={combineClasses([styles.wrapper, ...wrapperClasses])} id={identifier} ref={ref} initial={isBrowserContext() ? "hidden" : "shown"} animate={controls} variants={selectVariants} transition={{ duration: 0.4 }}>
            <div className={styles.inner}>
                {/* Filters */}
                {isBrowserContext() && viewState === "LOADED" ? (
                    <div className={styles.tags}>
                        <button tabIndex={0} onClick={clearFilters} className={combineClasses([styles.tag, noFiltersClass])}>
                            <Dictionary name="LOGOS_FILTER.ALL" />
                        </button>

                        {filters && filters?.map((filter: any) => {
                            const currentFilterClass = currentFilters.includes(filter.id.toString()) ? styles.is_active : undefined;

                            return (
                                <button key={filter.id} tabIndex={0} onClick={() => handleTagClick(filter)} className={combineClasses([styles.tag, currentFilterClass])}>
                                    {filter.label}
                                </button>
                            );
                        })}
                    </div>
                ) : null}

                {/* Logos */}
                <div className={styles.list}>
                    {items && items.map((logo, index) => {
                        return (
                            <Logo key={logo.contentUdi} {...logo} />
                        );
                    })}
                </div>
            </div>

            {props.settings.bubbleBackground && props.settings.bubbleBackground.length > 0 ? (
                <BubbleBackground {...props.settings.bubbleBackground[0]} />
            ) : null}
        </motion.section>
    );
};

export default Logos;